import { useState } from "react";
import { useTranslations } from "next-intl";
import { useReloadUser } from "@hooks/use-modify-user";
import { Button, Offcanvas } from "@components/core";
import SizeReferenceToggle from "@components/views/Profile/ManageSizes/shared/SizeReferenceToggle";
import sizeTypes from "@utils/constants/sizeTypes";
import { putUserSizeReference } from "@utils/call-api/users-call";
import SizeEquivalenceTable from "./SizeEquivalenceTable";
import SizeLengthEquivalenceTable from "./SizeLengthEquivalenceTable";
import { useCurrentUser } from "@hooks/use-current-user";

function SizesGuide({ sizeType, sizeReference }) {
  const t = useTranslations("common");

  const user = useCurrentUser();
  const reloadUser = useReloadUser();

  const [showSizesGuide, setShowSizesGuide] = useState(false);
  const [selectedSizeReference, setSelectedSizeReference] = useState(sizeReference);
  const [loading, setLoading] = useState(false);

  function closeSizesGuide() {
    setSelectedSizeReference(sizeReference);
    setShowSizesGuide(false);
  }

  async function updateUserSizeReference() {
    if (selectedSizeReference === sizeReference) return;

    setLoading(true);

    try {
      await putUserSizeReference(selectedSizeReference);
      await reloadUser();
    } catch (err) {
      await reloadUser();
    } finally {
      setLoading(false);
    }
  }

  function displayDifferentSizeReferenceWarning() {
    if (selectedSizeReference === sizeReference) return null;

    return (
      <div className="mt-4 d-flex flex-column gap-2 justify-content-center">
        <p className="mb-0 text-center font-size-sm text-gray">
          {t("size_guide_offcanvas.how_to_display_sizes")}
        </p>

        <Button
          variant="link-primary"
          className="no-underline"
          size="sm"
          onClick={updateUserSizeReference}
          loading={loading}
          trackingName="SizeGuideChangePreference"
          eventAdditionalData={{ user_size_reference: selectedSizeReference, size_type: sizeType }}
        >
          {t("size_guide_offcanvas.always_display_in", {
            size_reference_selected: selectedSizeReference,
          })}
        </Button>
      </div>
    );
  }

  if (sizeType === sizeTypes.size_tu) return null;

  return (
    <section>
      <Button
        variant="link-primary"
        className="no-underline"
        onClick={() => setShowSizesGuide(true)}
        trackingName="ProductPageSizeGuide"
        eventAdditionalData={{ user_size_reference: sizeReference, size_type: sizeType }}
      >
        {t("size_guide_offcanvas.show_size_guide_button")}
      </Button>

      <Offcanvas
        title={t("size_guide_offcanvas.title")}
        show={showSizesGuide}
        onHide={closeSizesGuide}
        placement="start"
      >
        <div className="pt-5 px-4 text-center">
          <h5>{t("size_guide_offcanvas.subtitle")}</h5>
          <SizeEquivalenceTable
            sizeType={sizeType}
            selectedSizeReference={selectedSizeReference}
          />
          <SizeLengthEquivalenceTable sizeType={sizeType} />
          {user && sizeType === sizeTypes.size_fr && (
            <div className="mt-4 d-flex flex-column justify-content-center align-items-center gap-3">
              <SizeReferenceToggle
                name="size-reference-toggle"
                title={t("size_guide_offcanvas.sizes_display")}
                sizeReference={selectedSizeReference}
                onChange={setSelectedSizeReference}
              />
              {displayDifferentSizeReferenceWarning()}
            </div>
          )}
        </div>
      </Offcanvas>
    </section>
  );
}

export default SizesGuide;
